import {
  decodePDFRawStream,
  PDFArray,
  PDFHexString,
  PDFName,
  PDFRawStream,
  PDFString,
  StandardFonts,
} from 'pdf-lib';

const latin = (bytes) => Array.from(bytes, (b) => String.fromCharCode(b)).join('');
// Tokenize content without interpreting escaped parentheses or binary string bytes as operators.
function tokens(text) {
  const result = [];
  let i = 0;
  while (i < text.length) {
    if (/\s/.test(text[i])) {
      i += 1;
      continue;
    }
    if (text[i] === '%') {
      while (i < text.length && text[i] !== '\n') i += 1;
      continue;
    }
    const start = i;
    if (text[i] === '(') {
      let depth = 1;
      i += 1;
      while (i < text.length && depth) {
        if (text[i] === '\\') {
          i += 2;
          continue;
        }
        if (text[i] === '(') depth += 1;
        if (text[i] === ')') depth -= 1;
        i += 1;
      }
      if (depth) throw new Error('Unclosed PDF string.');
      result.push({ start, end: i, bytes: PDFString.of(text.slice(start + 1, i - 1)).asBytes() });
    } else if (text[i] === '<' && text[i + 1] !== '<') {
      i = text.indexOf('>', i) + 1;
      if (!i) throw new Error('Unclosed PDF hex string.');
      result.push({
        start,
        end: i,
        bytes: PDFHexString.of(text.slice(start + 1, i - 1).replace(/\s/g, '')).asBytes(),
      });
    } else {
      if ('[]<>'.includes(text[i])) i += 1;
      else {
        i += 1;
        while (i < text.length && !/[\s[\]<>()/]/.test(text[i])) i += 1;
      }
      result.push({ start, end: i, word: text.slice(start, i) });
    }
  }
  return result;
}
function unicodeMap(font) {
  const cmap = font?.lookup(PDFName.of('ToUnicode'));
  if (!(cmap instanceof PDFRawStream)) return null;
  const text = latin(decodePDFRawStream(cmap).decode());
  const map = new Map();
  for (const block of text.matchAll(/beginbfrange([\s\S]*?)endbfrange/g)) {
    for (const m of block[1].matchAll(/<([\da-f]+)>\s*<([\da-f]+)>\s*<([\da-f]+)>/gi)) {
      const lo = parseInt(m[1], 16);
      const hi = parseInt(m[2], 16);
      const base = parseInt(m[3], 16);
      if (hi > 255 || hi - lo > 255) continue;
      for (let n = lo; n <= hi; n += 1) map.set(n, String.fromCodePoint(base + n - lo));
    }
  }
  for (const block of text.matchAll(/beginbfchar([\s\S]*?)endbfchar/g)) {
    for (const m of block[1].matchAll(/<([\da-f]+)>\s*<([\da-f]+)>/gi)) {
      if (m[1].length <= 2 && m[2].length === 4)
        map.set(parseInt(m[1], 16), String.fromCharCode(parseInt(m[2], 16)));
    }
  }
  return map;
}
function inspectPageSummary(pdf, pageIndex) {
  const page = pdf.getPage(pageIndex);
  const fonts = page.node.Resources()?.lookup(PDFName.of('Font'));
  const raw = page.node.Contents();
  const contents =
    raw instanceof PDFArray ? raw.asArray().map((ref) => pdf.context.lookup(ref)) : [raw];
  const matches = { min: [], max: [] };
  const streams = [];
  const lines = [];
  for (const stream of contents) {
    if (!(stream instanceof PDFRawStream)) continue;
    const text = latin(decodePDFRawStream(stream).decode());
    const streamIndex = streams.push({ stream, text }) - 1;
    const list = tokens(text);
    let fontName = '';
    let fontSize = 0;
    let arrayStart = -1;
    let pieces = [];
    let x = 0;
    let y = 0;
    const finish = () => {
      const decoded = pieces.map((piece) => piece.decoded).join('');
      if (pieces.length)
        lines.push({ ...pieces[0], sentence: decoded, end: pieces[pieces.length - 1].end });
      const match = /^\s*The\s+(minimum|maximum)\s+heart\s+rate\s+was\s+(\d+)\s+bpm\b/i.exec(
        decoded
      );
      if (match && pieces.length) {
        const key = match[1].toLowerCase() === 'minimum' ? 'min' : 'max';
        matches[key].push({
          ...pieces[0],
          value: Number(match[2]),
          sentence: decoded,
          end: pieces[pieces.length - 1].end,
        });
      }
      pieces = [];
    };
    for (let i = 0; i < list.length; i += 1) {
      const token = list[i];
      if (token.word === 'BT') pieces = [];
      if (token.word === 'ET') finish();
      if (pieces.length && ['Tm', 'Td', 'TD', 'T*'].includes(token.word)) finish();
      if (token.word === 'Tm') {
        x = Number(list[i - 2]?.word);
        y = Number(list[i - 1]?.word);
      }
      if (token.word === 'Tf') {
        fontName = list[i - 2]?.word?.slice(1);
        fontSize = Number(list[i - 1]?.word);
      }
      if (token.word === '[') arrayStart = i;
      if (!['TJ', 'Tj'].includes(token.word)) continue;
      const startIndex = token.word === 'TJ' ? arrayStart : i - 1;
      if (startIndex < 0) continue;
      const font = fonts?.lookup(PDFName.of(fontName));
      const map = unicodeMap(font);
      const bytes = list.slice(startIndex, i).flatMap((t) => (t.bytes ? Array.from(t.bytes) : []));
      const decoded = bytes
        .map((b) => (map ? (map.get(b) ?? '\ufffd') : String.fromCharCode(b)))
        .join('');
      pieces.push({
        x,
        y,
        decoded,
        streamIndex,
        start: list[startIndex].start,
        end: token.end,
        fontName,
        fontSize,
        map,
        font,
      });
    }
  }
  // Table-style summary rows are separate from the upper Heart Rate box.
  const summaryHeader = lines.find((line) => /^\s*Summary:\s*$/i.test(line.sentence));
  if (summaryHeader) {
    for (let i = 0; i < lines.length; i += 1) {
      const first = lines[i];
      if (first.y >= summaryHeader.y || !/^\s*(?:Min|Max)\b/i.test(first.sentence)) continue;
      const parts = [first];
      for (let j = i + 1; j < lines.length; j += 1) {
        const next = lines[j];
        if (
          next.streamIndex !== first.streamIndex ||
          Math.abs(next.y - first.y) > 0.5 ||
          Math.abs(next.x - first.x) > 0.5 ||
          /^\s*(?:Min|Max|Avg)\.\s*HR:/i.test(next.sentence)
        )
          break;
        parts.push(next);
      }
      const sentence = parts.map((part) => part.sentence).join('');
      const match = /^(\s*(min|max)\.?\s*(?:HR|Heart\s+Rate)\s*:\s*)(\d+)\s*BPM\b/i.exec(sentence);
      if (match)
        matches[match[2].toLowerCase()].push({
          ...first,
          sentence,
          parts,
          value: Number(match[3]),
          numberStart: match[1].length,
          numberLength: match[3].length,
        });
    }
  }
  const recognized = matches.min.length === 1 && matches.max.length === 1;
  return {
    recognized,
    min: recognized ? matches.min[0].value : null,
    max: recognized ? matches.max[0].value : null,
    matches,
    streams,
    lines,
    pageIndex,
  };
}
function inspectPageHeartRateBox(pdf, pageIndex) {
  const info = inspectPageSummary(pdf, pageIndex);
  const matches = { min: [], max: [] };
  const summaryHeader = info.lines.find((line) => /^\s*Summary:\s*$/i.test(line.sentence));
  const heartHeader = info.lines.find((line) => /^\s*Heart Rate\s*$/i.test(line.sentence));
  const hrvHeader = info.lines.find((line) => /^\s*HRV\s*$/i.test(line.sentence));
  const inBox = (line) =>
    (!summaryHeader || line.y > summaryHeader.y) &&
    (!heartHeader || line.y < heartHeader.y) &&
    (!hrvHeader || !heartHeader || hrvHeader.y >= heartHeader.y || line.y > hrvHeader.y) &&
    line.x < pdf.getPage(pageIndex).getWidth() / 3;
  for (const key of ['min', 'max']) {
    const labels = info.lines
      .filter(inBox)
      .filter((line) => new RegExp(`^\\s*${key}\\.\\s*HR:\\s*$`, 'i').test(line.sentence));
    if (labels.length !== 1) continue;
    const label = labels[0];
    matches[key] = info.lines
      .filter(inBox)
      .filter(
        (line) =>
          line.x > label.x &&
          Math.abs(line.y - label.y) < 3 &&
          /^\s*\d+\s+BPM(?:\s+@\d{2}:\d{2}:\d{2}(?:\s*\(\d+\))?)?\s*$/i.test(line.sentence)
      )
      .map((line) => ({ ...line, value: Number(line.sentence.match(/\d+/)[0]) }));
  }
  const recognized = matches.min.length === 1 && matches.max.length === 1;
  return {
    ...info,
    matches,
    recognized,
    min: recognized ? matches.min[0].value : null,
    max: recognized ? matches.max[0].value : null,
  };
}
function locateSection(pdf, inspect) {
  const pages = Array.from({ length: Math.min(3, pdf.getPageCount()) }, (_, index) =>
    inspect(pdf, index)
  );
  const found = pages.filter((info) => info.recognized);
  if (found.length === 1) return found[0];
  return { ...pages[0], recognized: false, min: null, max: null };
}
export function inspectSummary(pdf, pageIndex = null) {
  if (pageIndex !== null) return inspectPageSummary(pdf, pageIndex);
  return locateSection(pdf, inspectPageSummary);
}
export function inspectHeartRateBox(pdf) {
  return locateSection(pdf, inspectPageHeartRateBox);
}
export async function updateHeartRateBox(pdf, expected) {
  return updateValues(pdf, expected, inspectHeartRateBox(pdf), true);
}
export async function updateSummary(pdf, expected) {
  return updateValues(pdf, expected, inspectSummary(pdf), false);
}
async function updateValues(pdf, expected, info, box) {
  if (!info.recognized)
    throw new Error(
      box
        ? 'The Heart Rate box on the first three pages values could not be uniquely identified.'
        : 'The minimum/maximum summary sentences on the first three pages could not be uniquely identified.'
    );
  for (const key of ['min', 'max']) {
    if (!Number.isInteger(expected[key]) || expected[key] < 0)
      throw new Error('Invalid HR report limits.');
  }
  const patches = [];
  const targets = [];
  for (const key of ['min', 'max']) {
    const item = info.matches[key][0];
    if (item.value === expected[key]) continue;
    if (item.parts) {
      const digits = String(expected[key]);
      let offset = 0;
      let consumed = 0;
      const numberEnd = item.numberStart + item.numberLength;
      for (const part of item.parts) {
        const end = offset + part.sentence.length;
        const startInPart = Math.max(0, item.numberStart - offset);
        const endInPart = Math.min(part.sentence.length, numberEnd - offset);
        if (startInPart < endInPart) {
          const count = endInPart - startInPart;
          const portion =
            end >= numberEnd ? digits.slice(consumed) : digits.slice(consumed, consumed + count);
          consumed += count;
          const replacement =
            part.sentence.slice(0, startInPart) + portion + part.sentence.slice(endInPart);
          if (replacement !== part.sentence) targets.push({ item: part, replacement });
        }
        offset = end;
      }
    } else {
      const replacement = box
        ? item.sentence.replace(/\d+/, String(expected[key]))
        : item.sentence.replace(/(was\s+)\d+/i, `$1${expected[key]}`);
      targets.push({ item, replacement });
    }
  }
  for (const { item, replacement } of targets) {
    const reverse = item.map && new Map(Array.from(item.map, ([b, char]) => [char, b]));
    let command;
    if (
      reverse &&
      replacement.length === item.sentence.length &&
      Array.from(replacement).every((char) => reverse.has(char))
    ) {
      // Preserve the source's character positioning, including split sentences.
      const original = info.streams[item.streamIndex].text.slice(item.start, item.end);
      let offset = 0;
      const edits = [];
      for (const token of tokens(original)) {
        if (!token.bytes) continue;
        const encoded = Array.from(token.bytes, () => {
          const char = replacement[offset];
          offset += 1;
          return reverse.get(char).toString(16).padStart(2, '0');
        }).join('');
        edits.push({ start: token.start, end: token.end, value: `<${encoded}>` });
      }
      command = original;
      for (const edit of edits.reverse())
        command = command.slice(0, edit.start) + edit.value + command.slice(edit.end);
    } else if (reverse && Array.from(replacement).every((char) => reverse.has(char))) {
      command = `<${Array.from(replacement, (char) => reverse.get(char).toString(16).padStart(2, '0')).join('')}> Tj`;
    } else if (reverse) {
      // Keep the original sentence font. Only a missing digit uses the fallback;
      // adjust its advance to the source font's digit width.
      const font = await pdf.embedFont(StandardFonts.HelveticaBold);
      const name = pdf.getPage(info.pageIndex).node.newFontDictionary('HRSummary', font.ref);
      const widths = item.font.lookup(PDFName.of('Widths'));
      const first = item.font.lookup(PDFName.of('FirstChar'))?.asNumber();
      const digit = reverse.get('0') ?? reverse.get('1');
      const width = widths?.lookup(digit - first)?.asNumber();
      command = Array.from(replacement, (char) => {
        if (reverse.has(char)) return `<${reverse.get(char).toString(16).padStart(2, '0')}> Tj`;
        if (!/\d/.test(char) || !Number.isFinite(width))
          throw new Error('The summary font cannot represent the replacement text.');
        const adjustment = font.widthOfTextAtSize(char, 1000) - width;
        return `${name} ${item.fontSize} Tf [${font.encodeText(char)} ${adjustment}] TJ /${item.fontName} ${item.fontSize} Tf`;
      }).join(' ');
    } else {
      const font = await pdf.embedFont(StandardFonts.HelveticaBold);
      const name = pdf.getPage(info.pageIndex).node.newFontDictionary('HRSummary', font.ref);
      command = `${name} ${item.fontSize} Tf ${font.encodeText(replacement)} Tj /${item.fontName} ${item.fontSize} Tf`;
    }
    patches.push({ ...item, command });
  }
  for (let index = 0; index < info.streams.length; index += 1) {
    const changes = patches
      .filter((p) => p.streamIndex === index)
      .sort((a, b) => b.start - a.start);
    if (!changes.length) continue;
    const original = info.streams[index];
    let text = original.text;
    for (const patch of changes)
      text = text.slice(0, patch.start) + patch.command + text.slice(patch.end);
    const replacement = pdf.context.flateStream(
      Uint8Array.from(text, (char) => char.charCodeAt(0))
    );
    const ref = pdf.context.getObjectRef(original.stream);
    if (!ref) throw new Error('Unsupported inline PDF content stream.');
    pdf.context.assign(ref, replacement);
  }
}
